/**
 * Shared pagination math for admin list views (Referrers / Sponsors /
 * Employers, and any future list that pages a fully-fetched set).
 *
 * The endpoints behind these lists already return the FULL filtered set
 * (no server-side windowing), so paging happens client-side over the
 * fetched array. These helpers are the single source of truth for the
 * page math so every list clamps, slices, and reports counts the same
 * way — and so the logic is unit-testable without rendering React.
 *
 * Page size: 25 by default. At "potentially hundreds" of org records a
 * page of 25 keeps the row actions usable on a phone, matches the
 * density of the surrounding admin tables, and keeps a full render to
 * one screenful of DOM nodes; admins who want a longer view can pick
 * 50/100 from the pager (state is per-list, per-session only).
 */

export const DEFAULT_LIST_PAGE_SIZE = 25;

export const LIST_PAGE_SIZE_OPTIONS: readonly number[] = [10, 25, 50, 100];

/** Number of pages needed for `total` items at `pageSize` (always >= 1). */
export function pageCountFor(total: number, pageSize: number): number {
  const size = pageSize > 0 ? pageSize : DEFAULT_LIST_PAGE_SIZE;
  const count = Number.isFinite(total) && total > 0 ? Math.floor(total) : 0;
  return Math.max(1, Math.ceil(count / size));
}

/**
 * Clamp a 1-based page number into the valid range for the current set.
 * When the underlying set shrinks (rows archived/removed, filter
 * narrowed), a page that no longer exists clamps DOWN to the last real
 * page instead of stranding the admin on an empty page N.
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  const pages = pageCountFor(total, pageSize);
  const p = Number.isFinite(page) ? Math.floor(page) : 1;
  if (p < 1) return 1;
  if (p > pages) return pages;
  return p;
}

export interface PageSlice<T> {
  /** The items on the (clamped) current page. */
  pageItems: T[];
  /** The clamped 1-based page actually being shown. */
  page: number;
  /** Total number of pages (>= 1). */
  pageCount: number;
  /** Total items in the full (filtered) set. */
  total: number;
  /** 1-based position of the first shown item; 0 when the set is empty. */
  start: number;
  /** 1-based position of the last shown item; 0 when the set is empty. */
  end: number;
}

/**
 * Slice one page out of a fully-fetched list. `page` is clamped against
 * the CURRENT set size, so callers can pass a stale page number after
 * the set shrank and still get the last valid page's rows.
 */
export function paginateSlice<T>(items: readonly T[], page: number, pageSize: number): PageSlice<T> {
  const size = pageSize > 0 ? pageSize : DEFAULT_LIST_PAGE_SIZE;
  const total = items.length;
  const clamped = clampPage(page, total, size);
  const offset = (clamped - 1) * size;
  const pageItems = items.slice(offset, offset + size);
  return {
    pageItems,
    page: clamped,
    pageCount: pageCountFor(total, size),
    total,
    start: total === 0 ? 0 : offset + 1,
    end: total === 0 ? 0 : offset + pageItems.length,
  };
}
